/**
 * DrumKitSwap — perform the same beat with another kit.
 *
 * Input: the four parts from DrumSeparator ({kick, snare, toms, rest}). For
 * kick / snare / toms it detects the hits (onset time + velocity) on the
 * ISOLATED part — far more reliable than on the mix — and re-triggers them
 * with the chosen kit's one-shots; hi-hat + cymbals stay original (studio
 * drum-replacement practice: cymbals re-triggered from samples sound fake).
 * Output: {kick, snare, toms, rest, mix} AudioBuffers, `blend` 0..1 mixes the
 * original part back in.
 *
 *   const hits = DrumKitSwap.detect(parts)          // {kick:[{t,v}], snare:[…], toms:[…]}
 *   const out  = await DrumKitSwap.render(parts, hits, { kit: 'acoustic', blend: 0, tune: 0 })
 *
 * Kits: three SYNTHESISED (deterministic, ~zero bytes to download), eight
 * SAMPLED GM kits (v1.2, 2026-08-16: FluidR3_GM.sf2 (MIT) one-shots rendered by
 * scripts in the training lab → /vendor/drum-kits/{kit}/{voice}-{soft|hard}.wav,
 * ~270–1600 KB per kit, fetched only when chosen), and the user's own three
 * samples. `tune: 'auto'` matches the new kick / tom pitch to the ORIGINAL part
 * (autocorrelation f0 around the hits) — the groove keeps its tonal centre.
 * Everything renders off the UI thread (OfflineAudioContext + arithmetic placement).
 */
(function(){
'use strict';

// ── onset detection on an isolated part: spectral flux (log-magnitude), local
// peak picking, adaptive threshold, min-gap. Velocity = local RMS after onset,
// normalised to the part's loudest hit. Threshold is deliberately higher on
// snare (its model bleeds hi-hat ghosts — 127 onsets / 30 s on the demo).
// minVel drops ghosts/bleed after normalisation to the loudest hit — the
// separated snare part still carries hi-hat ghosts (demo: 119 raw peaks / 30 s
// vs ~50 real hits), and the kick part carries snare-body thumps.
var DEF = { kick: { minGap: 0.08, thr: 1.8, minVel: 0.12 }, snare: { minGap: 0.07, thr: 2.6, minVel: 0.18 }, toms: { minGap: 0.08, thr: 2.6, minVel: 0.18 } };
var HOP = 256, WIN = 1024;

function hann(n){ var w = new Float32Array(n); for (var i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / n); return w; }
var W = hann(WIN);

function fluxEnvelope(mono){
    // 32-band log-spectrum flux via a cheap DFT on WIN=1024 (bands ≈ mel-ish
    // by summing bins geometrically). Good enough for percussive onsets.
    var frames = Math.floor((mono.length - WIN) / HOP) + 1;
    var env = new Float32Array(Math.max(frames, 0));
    var re = new Float32Array(WIN), im = new Float32Array(WIN);
    var prev = null;
    // band edges (bins), geometric from 2 to WIN/2
    var edges = []; for (var b = 0; b <= 32; b++) edges.push(Math.round(2 * Math.pow(WIN / 4, b / 32)));
    for (var f = 0; f < frames; f++) {
        var off = f * HOP;
        for (var i = 0; i < WIN; i++) { re[i] = mono[off + i] * W[i]; im[i] = 0; }
        fft(re, im);
        var bands = new Float32Array(32);
        for (var b2 = 0; b2 < 32; b2++) {
            var s = 0; for (var k = edges[b2]; k < edges[b2 + 1]; k++) s += Math.sqrt(re[k] * re[k] + im[k] * im[k]);
            bands[b2] = Math.log(1 + 10 * s);
        }
        if (prev) { var fl = 0; for (var b3 = 0; b3 < 32; b3++) { var d = bands[b3] - prev[b3]; if (d > 0) fl += d; } env[f] = fl; }
        prev = bands;
    }
    return env;
}
function fft(re, im){
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) { var bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit; if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; } }
    for (var size = 2; size <= n; size <<= 1) { var half = size >> 1, ang = 2 * Math.PI / size, wR = Math.cos(ang), wI = Math.sin(ang);
        for (var s = 0; s < n; s += size) { var cR = 1, cI = 0; for (var k = 0; k < half; k++) { var a = s + k, b = a + half; var vR = re[b] * cR - im[b] * cI, vI = re[b] * cI + im[b] * cR; re[b] = re[a] - vR; im[b] = im[a] - vI; re[a] += vR; im[a] += vI; var nR = cR * wR - cI * wI; cI = cR * wI + cI * wR; cR = nR; } } }
}

function detectPart(buf, cfg){
    var sr = buf.sampleRate, L = buf.getChannelData(0), R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : L;
    var mono = new Float32Array(L.length); for (var i = 0; i < L.length; i++) mono[i] = 0.5 * (L[i] + R[i]);
    var env = fluxEnvelope(mono);
    if (!env.length) return [];
    // adaptive threshold: local mean over ±0.5 s + thr * mean-abs-deviation, both
    // from prefix sums (one pass) — the per-peak sort of the old median took ~6 s
    // on a 4-minute track (QA 2026-08-16); a robust mean is indistinguishable here.
    var half = Math.round(0.5 * sr / HOP), hits = [], nF = env.length;
    var ps = new Float64Array(nF + 1); for (var i2 = 0; i2 < nF; i2++) ps[i2 + 1] = ps[i2] + env[i2];
    var localMean = function(f){ var a = Math.max(0, f - half), b = Math.min(nF, f + half); return (ps[b] - ps[a]) / Math.max(1, b - a); };
    var dev = new Float32Array(nF); for (var i3 = 0; i3 < nF; i3++) dev[i3] = Math.abs(env[i3] - localMean(i3));
    var pd = new Float64Array(nF + 1); for (var i4 = 0; i4 < nF; i4++) pd[i4 + 1] = pd[i4] + dev[i4];
    var minGapF = Math.round(cfg.minGap * sr / HOP), last = -1e9;
    for (var f = 1; f < nF - 1; f++) {
        if (env[f] <= env[f - 1] || env[f] < env[f + 1]) continue;             // local peak
        var a = Math.max(0, f - half), b = Math.min(nF, f + half);
        var med = localMean(f), mad = (pd[b] - pd[a]) / Math.max(1, b - a);
        if (env[f] < med + cfg.thr * (mad + 1e-6)) continue;
        if (f - last < minGapF) continue;
        last = f;
        // refine onset to the sample of steepest rise within the frame, velocity = RMS 30 ms after
        var s0 = f * HOP, s1 = Math.min(mono.length, s0 + Math.round(0.03 * sr)), e = 0;
        for (var n = s0; n < s1; n++) e += mono[n] * mono[n];
        hits.push({ t: s0 / sr, v: Math.sqrt(e / Math.max(1, s1 - s0)) });
    }
    // normalise to the 90th-percentile hit, not the loudest: one accent (or one
    // bleed spike) at 1.0 otherwise squashes every real hit to 0.1–0.3 and the
    // minVel gate eats the groove. Clamp to 1 after.
    var vs = hits.map(function(h){ return h.v; }).sort(function(a, b){ return a - b; });
    var ref = vs.length ? vs[Math.min(vs.length - 1, Math.floor(vs.length * 0.9))] : 0;
    if (ref > 0) hits.forEach(function(h){ h.v = Math.min(1, h.v / ref); });
    return hits.filter(function(h){ return h.v >= (cfg.minVel || 0); });
}

function detect(parts, opts){
    opts = opts || {};
    return {
        kick: detectPart(parts.kick, Object.assign({}, DEF.kick, opts.kick || {})),
        snare: detectPart(parts.snare, Object.assign({}, DEF.snare, opts.snare || {})),
        toms: detectPart(parts.toms, Object.assign({}, DEF.toms, opts.toms || {}))
    };
}

// ── kits: synthesised one-shots rendered into AudioBuffers once per kit/sr ──
// Each voice(ctx, t, vel, dest) schedules nodes; `tune` = semitones applied to
// the pitched parts (kick body, toms) so a kit can sit in the song's key.
// Noise one-shots are cached per (context, length): a 4-minute track fires
// ~600 snares, and generating a fresh 0.35 s buffer for each cost most of the
// 76 s swap time on the QA pass (2026-08-16).
var _noiseCache = new WeakMap();
function noiseBuf(ctx, sec){
    var m = _noiseCache.get(ctx); if (!m) { m = {}; _noiseCache.set(ctx, m); }
    var key = String(sec);
    if (m[key]) return m[key];
    var b = ctx.createBuffer(1, Math.ceil(sec * ctx.sampleRate), ctx.sampleRate); var d = b.getChannelData(0); var s = 22222;
    for (var i = 0; i < d.length; i++) { s = (s * 1664525 + 1013904223) >>> 0; d[i] = (s / 4294967296) * 2 - 1; }
    m[key] = b; return b;
}
function st(v){ return Math.pow(2, v / 12); }

var KITS = {
    acoustic: {
        kick: function(ctx, t, v, dest, tune){ var o = ctx.createOscillator(), g = ctx.createGain(); o.type = 'sine';
            var f0 = 62 * st(tune); o.frequency.setValueAtTime(f0 * 3, t); o.frequency.exponentialRampToValueAtTime(f0, t + 0.045);
            g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(0.9 * v, t + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28 + 0.15 * v);
            var c = ctx.createBufferSource(); c.buffer = noiseBuf(ctx, 0.01); var cg = ctx.createGain(); cg.gain.setValueAtTime(0.25 * v, t); cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.012);
            var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 1500;
            o.connect(g).connect(dest); c.connect(hp).connect(cg).connect(dest); o.start(t); o.stop(t + 0.5); c.start(t); },
        snare: function(ctx, t, v, dest){ var body = ctx.createOscillator(); body.type = 'triangle'; body.frequency.setValueAtTime(190, t); body.frequency.exponentialRampToValueAtTime(150, t + 0.06);
            var bg = ctx.createGain(); bg.gain.setValueAtTime(0.5 * v, t); bg.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
            var n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.35); var bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2400; bp.Q.value = 0.7;
            var ng = ctx.createGain(); ng.gain.setValueAtTime(0.7 * v, t); ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.18 + 0.1 * v);
            body.connect(bg).connect(dest); n.connect(bp).connect(ng).connect(dest); body.start(t); body.stop(t + 0.3); n.start(t); },
        toms: function(ctx, t, v, dest, tune, idx){ var f = [180, 130, 95][idx % 3] * st(tune); var o = ctx.createOscillator(); o.type = 'sine';
            o.frequency.setValueAtTime(f * 1.4, t); o.frequency.exponentialRampToValueAtTime(f, t + 0.08);
            var g = ctx.createGain(); g.gain.setValueAtTime(0.8 * v, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
            o.connect(g).connect(dest); o.start(t); o.stop(t + 0.5); }
    },
    tight: {
        kick: function(ctx, t, v, dest, tune){ var o = ctx.createOscillator(); o.type = 'sine'; var f0 = 55 * st(tune);
            o.frequency.setValueAtTime(f0 * 4, t); o.frequency.exponentialRampToValueAtTime(f0, t + 0.03);
            var g = ctx.createGain(); g.gain.setValueAtTime(v, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
            var c = ctx.createBufferSource(); c.buffer = noiseBuf(ctx, 0.006); var cg = ctx.createGain(); cg.gain.setValueAtTime(0.4 * v, t); cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.008);
            o.connect(g).connect(dest); c.connect(cg).connect(dest); o.start(t); o.stop(t + 0.3); c.start(t); },
        snare: function(ctx, t, v, dest){ var n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.2); var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 900;
            var ng = ctx.createGain(); ng.gain.setValueAtTime(0.9 * v, t); ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
            var o = ctx.createOscillator(); o.type = 'square'; o.frequency.value = 220; var og = ctx.createGain(); og.gain.setValueAtTime(0.25 * v, t); og.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
            n.connect(hp).connect(ng).connect(dest); o.connect(og).connect(dest); n.start(t); o.start(t); o.stop(t + 0.1); },
        toms: function(ctx, t, v, dest, tune, idx){ var f = [200, 150, 110][idx % 3] * st(tune); var o = ctx.createOscillator(); o.type = 'triangle';
            o.frequency.setValueAtTime(f * 1.3, t); o.frequency.exponentialRampToValueAtTime(f, t + 0.05);
            var g = ctx.createGain(); g.gain.setValueAtTime(0.8 * v, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2); o.connect(g).connect(dest); o.start(t); o.stop(t + 0.3); }
    },
    electronic: {
        kick: function(ctx, t, v, dest, tune){ var o = ctx.createOscillator(); o.type = 'sine'; var f0 = 45 * st(tune);
            o.frequency.setValueAtTime(f0 * 6, t); o.frequency.exponentialRampToValueAtTime(f0, t + 0.06);
            var g = ctx.createGain(); g.gain.setValueAtTime(v, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6 + 0.3 * v);
            var ws = ctx.createWaveShaper(); var curve = new Float32Array(256); for (var i = 0; i < 256; i++) { var x = i / 127.5 - 1; curve[i] = Math.tanh(2.2 * x); } ws.curve = curve;
            o.connect(ws).connect(g).connect(dest); o.start(t); o.stop(t + 1.2); },
        snare: function(ctx, t, v, dest){ var n = ctx.createBufferSource(); n.buffer = noiseBuf(ctx, 0.3); var bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1800; bp.Q.value = 1.2;
            var ng = ctx.createGain(); ng.gain.setValueAtTime(0.9 * v, t); ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
            var o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(240, t); o.frequency.exponentialRampToValueAtTime(160, t + 0.08); var og = ctx.createGain(); og.gain.setValueAtTime(0.5 * v, t); og.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
            n.connect(bp).connect(ng).connect(dest); o.connect(og).connect(dest); n.start(t); o.start(t); o.stop(t + 0.2); },
        toms: function(ctx, t, v, dest, tune, idx){ var f = [220, 165, 120][idx % 3] * st(tune); var o = ctx.createOscillator(); o.type = 'sine';
            o.frequency.setValueAtTime(f * 1.6, t); o.frequency.exponentialRampToValueAtTime(f * 0.9, t + 0.15);
            var g = ctx.createGain(); g.gain.setValueAtTime(0.85 * v, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45); o.connect(g).connect(dest); o.start(t); o.stop(t + 0.6); }
    }
};

function rms(buf){ var d = buf.getChannelData(0), s = 0; for (var i = 0; i < d.length; i += 4) s += d[i] * d[i]; return Math.sqrt(s / (d.length / 4)) + 1e-9; }

// ── Sampled kits (GM, FluidR3_GM.sf2 — MIT). Files: {base}/{kit}/{voice}-{layer}.wav,
//    voices kick / snare / tom-lo / tom-mid / tom-hi, layers soft / hard.
//    Decoded straight at the track's sample rate through an OfflineAudioContext
//    (no second resample), peak-normalised per voice on render (the files keep
//    soft/hard dynamics: hard = 0.9 peak, soft scaled by the same gain).
var SAMPLE_KITS = ['gm-standard', 'gm-room', 'gm-power', 'gm-electronic', 'gm-808', 'gm-jazz', 'gm-brush', 'gm-orchestra'];
var SLAYERS = [0.5, 1.0], SLAYER_NAME = { 0.5: 'soft', 1: 'hard' };
var TOM_NAMES = ['tom-hi', 'tom-mid', 'tom-lo'];        // index 0..2 == synth kits' hi → lo
var _sampleBase = '/vendor/drum-kits';
var _sampleKits = {};                                    // kit|sr → Promise<{voice-layer: [L, R]}> (in-flight too:
                                                         // a prefetch on select-change and the swap click must share ONE download)
function setSampleBase(u){ if (u) _sampleBase = String(u).replace(/\/+$/, ''); }
function isSampleKit(k){ return SAMPLE_KITS.indexOf(k) >= 0; }
function loadSampleKit(kit, sr){
    var key = kit + '|' + sr;
    if (_sampleKits[key]) return _sampleKits[key];
    _sampleKits[key] = fetchSampleKit(kit, sr).catch(function(e){ delete _sampleKits[key]; throw e; });   // a failed fetch may be retried
    return _sampleKits[key];
}
async function fetchSampleKit(kit, sr){
    var names = ['kick', 'snare'].concat(TOM_NAMES), files = [];
    names.forEach(function(v){ ['soft', 'hard'].forEach(function(l){ files.push(v + '-' + l); }); });
    var OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var loaded = await Promise.all(files.map(function(f){
        return fetch(_sampleBase + '/' + kit + '/' + f + '.wav').then(function(r){ if (!r.ok) throw new Error('kit_fetch_' + r.status); return r.arrayBuffer(); })
            .then(function(ab){ return new OAC(1, 1, sr).decodeAudioData(ab); })
            .then(function(b){ var d = b.getChannelData(0).slice(0); return [d, d]; });   // mono → both channels
    }));
    var out = {}; files.forEach(function(f, i){ out[f] = loaded[i]; });
    return out;
}
// playback-rate resample (linear) — semitone shift for tuned kick / toms
function shiftShot(shot, semis){
    if (!semis) return shot;
    var r = Math.pow(2, semis / 12), n = Math.floor(shot[0].length / r), L = new Float32Array(n), R = new Float32Array(n);
    for (var i = 0; i < n; i++) { var x = i * r, j = Math.floor(x), f = x - j, j1 = Math.min(j + 1, shot[0].length - 1); L[i] = shot[0][j] * (1 - f) + shot[0][j1] * f; R[i] = shot[1][j] * (1 - f) + shot[1][j1] * f; }
    return [L, R];
}
// f0 of a short percussive window (fmin..fmax Hz): one-pole low-pass at 2·fmax to
// drop the beater/stick click, then NORMALISED autocorrelation (the plain one is
// biased by the decay and read a demo kick as «no pitch» in 9 hits of 10).
// Window: 20 ms after the onset (past the transient), 120 ms long. 0 = untuned/noise.
function f0Of(d, from, sr, fmin, fmax){
    var n = Math.min(Math.round(0.12 * sr), d.length - from); if (n < 512) return 0;
    var a = 1 - Math.exp(-2 * Math.PI * (2 * fmax) / sr), y = 0, x = new Float32Array(n), i;
    for (i = 0; i < n; i++) { y += a * (d[from + i] - y); x[i] = y; }
    var minLag = Math.floor(sr / fmax), maxLag = Math.min(Math.floor(sr / fmin), n >> 1), best = 0, bestLag = 0;
    for (var lag = minLag; lag <= maxLag; lag++) {
        var c = 0, e1 = 0, e2 = 0;
        for (i = 0; i + lag < n; i++) { c += x[i] * x[i + lag]; e1 += x[i] * x[i]; e2 += x[i + lag] * x[i + lag]; }
        var den = Math.sqrt(e1 * e2); if (den < 1e-9) continue;
        c /= den; if (c > best) { best = c; bestLag = lag; }
    }
    return best > 0.5 && bestLag ? sr / bestLag : 0;
}
// Kick drums have no steady pitch (the fundamental sweeps down over ~100 ms:
// 140 → 80 → 55 Hz on the demo, and autocorrelation reads «no pitch» on every
// hit) — so for the kick the tonal centre is the energy-weighted log-frequency
// centroid of the 30–160 Hz band over the first ~93 ms (Hann, 4096-pt DFT on the
// band only). Measured the SAME way on the kit voice, the ratio is meaningful.
function lowCentroid(d, from, sr, fmin, fmax){
    var N = Math.min(4096, d.length - from); if (N < 1024) return 0;
    var num = 0, den = 0, w = new Float32Array(N), i;
    for (i = 0; i < N; i++) w[i] = (d[from + i] || 0) * (0.5 - 0.5 * Math.cos(2 * Math.PI * i / N));
    for (var f = fmin; f <= fmax; f += 2) {
        var re = 0, im = 0, k = 2 * Math.PI * f / sr;
        for (i = 0; i < N; i++) { re += w[i] * Math.cos(k * i); im -= w[i] * Math.sin(k * i); }
        var m = re * re + im * im; num += m * Math.log(f); den += m;
    }
    return den > 1e-9 ? Math.exp(num / den) : 0;
}
function medianF0(d, sr, hits, fmin, fmax, useCentroid){
    var top = (hits || []).slice().sort(function(a, b){ return b.v - a.v; }).slice(0, 16), fs = [];
    top.forEach(function(h){ var f = useCentroid ? lowCentroid(d, Math.round(h.t * sr), sr, fmin, fmax) : f0Of(d, Math.round((h.t + 0.02) * sr), sr, fmin, fmax); if (f) fs.push(f); });
    if (fs.length < 3) return 0;
    fs.sort(function(a, b){ return a - b; }); return fs[fs.length >> 1];
}
// tune (semitones) that moves the kit's kick / mid-tom onto the ORIGINAL part's f0
async function autoTune(parts, hits, kitName, sr){
    var out = { kick: 0, toms: 0 }, ranges = { kick: [30, 160], toms: [60, 400] }, clamp = { kick: 7, toms: 5 };
    for (var name in ranges) {
        var isKick = name === 'kick';
        var orig = medianF0(parts[name].getChannelData(0), sr, hits[name], ranges[name][0], ranges[name][1], isKick);
        if (!orig) continue;                                                // no clear pitch in the original → leave as is
        var shot = await renderOneShot(kitName, name, sr, 0, 1.0, 1);      // hard layer, mid tom
        var mine = isKick ? lowCentroid(shot[0], 0, sr, ranges[name][0], ranges[name][1]) : f0Of(shot[0], Math.round(0.02 * sr), sr, ranges[name][0], ranges[name][1]);
        if (!mine) continue;                                                // click-only kit voice → nothing to tune
        out[name] = Math.max(-clamp[name], Math.min(clamp[name], Math.round(12 * Math.log2(orig / mine))));
    }
    return out;
}

// Sample-player rendering (2026-08-16): render each kit voice ONCE per velocity
// layer into a short buffer, then place copies arithmetically. A 4-minute
// track fires ~1200 hits; scheduling 1200 live synth voices in one
// OfflineAudioContext took 68 s on the QA pass — placement is ~100× faster
// and is exactly how a sampled (SFZ) kit will be played later.
var LAYERS = [0.35, 0.55, 0.75, 1.0];          // velocity layers rendered per voice
var VOICE_SEC = { kick: 1.3, snare: 0.5, toms: 0.7 };
var _oneShotCache = {};                        // key: kit|part|sr|tune|layer → Float32Array[2]

// User kit: {kick, snare, toms} → AudioBuffer (mono/stereo, any rate). Resampled
// once to the track rate; velocity scales the gain (no layers — one shot each).
var _userKit = null, _userShots = {};
function setUserKit(k){ _userKit = k || null; _userShots = {}; }
function hasUserKit(){ return !!(_userKit && _userKit.kick && _userKit.snare && _userKit.toms); }
async function userShot(part, sr){
    var key = part + '|' + sr;
    if (_userShots[key]) return _userShots[key];
    var b = _userKit[part];
    if (b.sampleRate !== sr) {                       // OfflineAudioContext resample, once
        var oc = new OfflineAudioContext(2, Math.ceil(b.duration * sr), sr);
        var src = oc.createBufferSource(); src.buffer = b; src.connect(oc.destination); src.start(0);
        b = await oc.startRendering();
    }
    var L = b.getChannelData(0).slice(0), R = (b.numberOfChannels > 1 ? b.getChannelData(1) : b.getChannelData(0)).slice(0);
    var peak = 0; for (var i = 0; i < L.length; i += 4) { var a = Math.max(Math.abs(L[i]), Math.abs(R[i])); if (a > peak) peak = a; }
    if (peak > 0) { var g = 0.9 / peak; for (var j = 0; j < L.length; j++) { L[j] *= g; R[j] *= g; } }   // peak-normalise the sample
    _userShots[key] = [L, R]; return _userShots[key];
}

async function renderOneShot(kit, part, sr, tune, vel, tomIdx){
    if (kit === 'user') return userShot(part, sr);    // tune/tomIdx ignored for user samples (v1)
    var key = [kit, part, sr, tune, vel, tomIdx].join('|');
    if (_oneShotCache[key]) return _oneShotCache[key];
    if (isSampleKit(kit)) {
        var set = await loadSampleKit(kit, sr);
        var voice = part === 'toms' ? TOM_NAMES[(tomIdx || 0) % 3] : part;
        var raw = set[voice + '-' + (vel >= 0.75 ? 'hard' : 'soft')];
        var res0 = part === 'snare' ? raw : shiftShot(raw, tune || 0);
        _oneShotCache[key] = res0; return res0;
    }
    var len = Math.ceil(VOICE_SEC[part] * sr);
    var ctx = new OfflineAudioContext(2, len, sr);
    KITS[kit][part](ctx, 0, vel, ctx.destination, tune || 0, tomIdx || 0);
    var out = await ctx.startRendering();
    var res = [out.getChannelData(0).slice(0), out.numberOfChannels > 1 ? out.getChannelData(1).slice(0) : out.getChannelData(0).slice(0)];
    _oneShotCache[key] = res; return res;
}
function layerFor(v, layers){ layers = layers || LAYERS; var best = 0; for (var i = 1; i < layers.length; i++) if (Math.abs(layers[i] - v) < Math.abs(layers[best] - v)) best = i; return layers[best]; }

async function renderParts(parts, hits, kitName, sr, len, tune){
    var res = {}, names = ['kick', 'snare', 'toms'];
    for (var n = 0; n < names.length; n++) {
        var name = names[n], L = new Float32Array(len), R = new Float32Array(len), list = hits[name] || [];
        var isUser = kitName === 'user', layers = isSampleKit(kitName) ? SLAYERS : LAYERS;
        var tomCount = (name === 'toms' && !isUser) ? 3 : 1;
        var partTune = (tune && typeof tune === 'object') ? (tune[name] || 0) : (tune || 0);
        // pre-render every layer (and tom pitch) this part needs
        var shots = {};
        for (var li = 0; li < layers.length; li++) for (var ti = 0; ti < tomCount; ti++) shots[layers[li] + '|' + ti] = await renderOneShot(kitName, name, sr, partTune, layers[li], ti);
        for (var h = 0; h < list.length; h++) {
            var hit = list[h], vel = 0.35 + 0.65 * hit.v, layer = layerFor(vel, layers), ti2 = (name === 'toms' && !isUser) ? (h % 3) : 0;
            var shot = shots[layer + '|' + ti2], gain = isUser ? vel : vel / layer;      // user sample: plain velocity gain
            var start = Math.round(hit.t * sr), n2 = Math.min(shot[0].length, len - start);
            for (var i = 0; i < n2; i++) { L[start + i] += shot[0][i] * gain; R[start + i] += shot[1][i] * gain; }
        }
        var ac = window.__sharedAudioCtx || (window.__sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)());
        var b = ac.createBuffer(2, len, sr); b.getChannelData(0).set(L); b.getChannelData(1).set(R);
        // level-match the replaced part to the original part it replaces
        var g = rms(parts[name]) / rms(b); if (!isFinite(g)) g = 1; g = Math.min(g, 8);
        for (var c = 0; c < 2; c++) { var d = b.getChannelData(c); for (var k = 0; k < d.length; k++) d[k] *= g; }
        res[name] = b;
    }
    return res;
}

function mixInto(dst, a, ga, b, gb){
    for (var c = 0; c < 2; c++) { var o = dst.getChannelData(c), x = a.getChannelData(Math.min(c, a.numberOfChannels - 1)), y = b.getChannelData(Math.min(c, b.numberOfChannels - 1));
        for (var i = 0; i < o.length; i++) o[i] = ga * x[i] + gb * y[i]; }
}

async function render(parts, hits, opts){
    opts = opts || {};
    var blend = Math.max(0, Math.min(1, opts.blend || 0)), tune = opts.tune || 0;
    var sr = parts.kick.sampleRate, len = parts.kick.length;
    var ac = window.__sharedAudioCtx || (window.__sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)());
    var newParts = {};
    var kitName = opts.kit === 'user' && hasUserKit() ? 'user' : ((opts.kit && KITS[opts.kit]) || isSampleKit(opts.kit) ? opts.kit : 'acoustic');
    if (tune === 'auto') tune = kitName === 'user' ? 0 : await autoTune(parts, hits, kitName, sr);
    var rendered = await renderParts(parts, hits, kitName, sr, len, tune);
    ['kick', 'snare', 'toms'].forEach(function(name){
        var out = ac.createBuffer(2, len, sr);
        mixInto(out, rendered[name], 1 - blend, parts[name], blend);   // blend = how much ORIGINAL to keep
        newParts[name] = out;
    });
    newParts.rest = parts.rest;                                  // cymbals/hihat untouched
    var mix = ac.createBuffer(2, len, sr);
    for (var c = 0; c < 2; c++) { var m = mix.getChannelData(c);
        ['kick', 'snare', 'toms', 'rest'].forEach(function(n){ var d = newParts[n].getChannelData(Math.min(c, newParts[n].numberOfChannels - 1)); for (var k = 0; k < m.length; k++) m[k] += d[k]; }); }
    var peak = 0; for (var c2 = 0; c2 < 2; c2++) { var d2 = mix.getChannelData(c2); for (var k2 = 0; k2 < d2.length; k2 += 8) { var a = Math.abs(d2[k2]); if (a > peak) peak = a; } }
    if (peak > 0.98) { var g = 0.98 / peak; for (var c3 = 0; c3 < 2; c3++) { var d3 = mix.getChannelData(c3); for (var k3 = 0; k3 < d3.length; k3++) d3[k3] *= g; } }
    newParts.mix = mix;
    newParts.tune = (tune && typeof tune === 'object') ? tune : { kick: tune || 0, toms: tune || 0 };   // what was applied (auto → per part)
    return newParts;
}

window.DrumKitSwap = { detect: detect, render: render, kits: Object.keys(KITS).concat(SAMPLE_KITS), sampleKits: SAMPLE_KITS.slice(), setSampleBase: setSampleBase, preloadKit: function(kit, sr){ return isSampleKit(kit) ? loadSampleKit(kit, sr || 44100) : Promise.resolve(); }, setUserKit: setUserKit, hasUserKit: hasUserKit, version: '1.2.0' };
})();
